import React from 'react'
import { useIntl } from 'react-intl'
import { Tooltip } from 'antd'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { BatteryAvatar } from '../../../../design-system/BatteryAvatar'
import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

import { ADPFlag } from '../../../../components/adp/integrated-flag'
import { StatusBadge } from '../../../../design-system/StatusBadge'
import { STATUS_I18N_KEY } from '../../../TeamEnergyPage/components/StatusPill'
import { IUserFilteredInfo } from '../../../../types/users'

const INVITE_BADGES: Partial<Record<EmailInviteStatusEnum, { tone: 'info' | 'warning' | 'danger'; messageId: string }>> = {
  [EmailInviteStatusEnum.pending]: { tone: 'info', messageId: 'users.invitedPending' },
  [EmailInviteStatusEnum.expired]: { tone: 'danger', messageId: 'users.invitedExpired' },
  [EmailInviteStatusEnum.notInvited]: { tone: 'warning', messageId: 'users.uninvited' },
}

/** The invite badge this cell will draw, if any. Exported because the name column sizes itself to
 * its content, and the badge shares the row with the name without being able to shrink. */
export const inviteBadgeFor = (
  user: IUserFilteredInfo,
  companyPlatform: string | undefined,
  getInviteStatus: (user: IUserFilteredInfo) => EmailInviteStatusEnum
): { tone: 'info' | 'warning' | 'danger'; messageId: string } | undefined =>
  companyPlatform === PlatformEnum.Email && user.isGhostUser ? INVITE_BADGES[getInviteStatus(user)] : undefined

export interface INameCellProps {
  user: IUserFilteredInfo
  companyPlatform?: string
  getInviteStatus: (user: IUserFilteredInfo) => EmailInviteStatusEnum
  showEnergy?: boolean
}

export const NameCell = ({ user, companyPlatform, getInviteStatus, showEnergy }: INameCellProps): React.ReactElement => {
  const { formatMessage } = useIntl()

  const inviteBadge = inviteBadgeFor(user, companyPlatform, getInviteStatus)

  const energy = showEnergy ? user.energy : undefined
  const energyLabel = energy ? formatMessage({ id: STATUS_I18N_KEY[energy.mode] }) : undefined

  return (
    <div className="vt-users-name-cell">
      {energy && energyLabel ? (
        <Tooltip title={energyLabel} placement="bottomLeft" mouseEnterDelay={0.35}>
          <span style={{ display: 'inline-flex', minWidth: 0 }}>
            <BatteryAvatar id={user.id} avatar={user.imageUrl} name={user.name} size={32} score={energy.score} label={energyLabel} isShowingName={true} isLinkToPage={true} />
          </span>
        </Tooltip>
      ) : (
        <UserAvatar id={user.id} avatar={user.imageUrl} name={user.name} isShowingName={true} isLinkToPage={true} shape="circle" />
      )}
      {inviteBadge && <StatusBadge tone={inviteBadge.tone}>{formatMessage({ id: inviteBadge.messageId })}</StatusBadge>}
      {user.adpIntegrated && <ADPFlag />}
    </div>
  )
}
